import {
  ApiError,
  createUploadSession,
  finalizeUpload,
  renewUploadSession,
  type UploadSessionView,
} from '@/lib/api';
import {
  BlobError,
  blockIdFor,
  commitBlockList,
  listStagedBlocks,
  stageBlock,
} from '@/lib/blockBlob';

/**
 * One file, from a browser file picker into the uploader's own storage account
 * (SPEC §7.1, UPL-1..5).
 *
 * The whole sequence lives here rather than in a component because none of it
 * is React: open a session → stage blocks → commit the block list → mark the
 * session committed → finalize. `apps/web` has no other module that talks to
 * two systems in one act, and that is exactly why it is testable on its own.
 *
 * **Bytes never pass through the API.** It hands out a short-lived, blob-scoped
 * SAS and steps out of the way, which is what makes a multi-gigabyte transfer
 * possible at all (UPL-3) — a Functions app is not a file server.
 *
 * **Retrying is resuming.** There is one path: PATCH for a fresh credential,
 * ask blob storage which blocks already landed, and send only the rest. That is
 * why a failure carries its `sessionId` — a retry that opened a new session
 * would allocate a new `fileId`, strand every block already paid for, and write
 * a second `file.upload_started` row into the audit log.
 *
 * **Resume is bounded by the page.** A `File` handle does not survive a reload,
 * so nothing here persists a session and nothing above it should promise that a
 * transfer outlives the tab.
 */

/** Bytes only move during `staging`; the rest are round trips worth naming. */
export type UploadPhase = 'opening' | 'staging' | 'committing' | 'finalizing';

export interface UploadProgress {
  phase: UploadPhase;
  bytesSent: number;
  totalBytes: number;
}

export interface UploadTargetRef {
  spaceId: string;
  folderId: string;
}

export interface UploadOptions {
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
  /** Fires once, as early as possible, so a later failure has a session to resume. */
  onSessionOpened?: (sessionId: string) => void;
  /** Resume an existing session rather than opening a new one (UPL-2). */
  resumeSessionId?: string;
  /**
   * FIL-8c — the document this upload was signed from, as its file handle.
   *
   * Carried on `UploadOptions` rather than as a parameter because it belongs to
   * the *file* being sent, not to the transfer: a resume has to send it again,
   * and it is read only at the finalize where the audit row is written.
   */
  signedFromFileId?: string;
}

export interface UploadResult {
  sessionId: string;
  fileId: string;
}

/**
 * What went wrong, and whether trying again could possibly help.
 *
 * `permanent` is the distinction the UI actually renders: a observer space
 * (CON-13) will refuse forever and offering a retry would be a lie, whereas a
 * dropped block is worth another go. `sessionId` is present whenever a session
 * got as far as existing, which is what makes that retry a resume.
 */
export class UploadFailure extends Error {
  /** HTTP status, or `0` for a network-level failure. */
  readonly status: number;
  readonly permanent: boolean;
  readonly sessionId?: string;
  /**
   * The API's own words, when it gave any — and **only** then.
   *
   * Its refusals are written to be read: "Your agency can receive files in this
   * space, but not add them" tells somebody what to do next in a way no status
   * code does, and paraphrasing it here would lose that. Absent for a wire
   * failure, where the caller has copy of its own to fall back on. `message` is
   * for developers; this is the string a person sees.
   */
  readonly detail?: string;

  constructor(
    message: string,
    options: { status: number; permanent: boolean; sessionId?: string; detail?: string },
  ) {
    super(message);
    this.name = 'UploadFailure';
    this.status = options.status;
    this.permanent = options.permanent;
    if (options.sessionId !== undefined) this.sessionId = options.sessionId;
    if (options.detail !== undefined) this.detail = options.detail;
  }
}

/**
 * Three at a time.
 *
 * The uplink is the bottleneck for an agency pushing evidence out, not the
 * connection count — past three, more parallelism buys nothing and multiplies
 * what a mid-flight failure costs to redo.
 */
const BLOCK_CONCURRENCY = 3;

/** Two retries after the first try. Beyond that it is not a blip. */
const BLOCK_ATTEMPTS = 3;

/**
 * Renew a SAS with five minutes left on it.
 *
 * Wide enough to cover a block already in flight on a slow space plus modest
 * clock skew; narrow enough that a forty-minute upload renews once rather than
 * four times against the API's one-hour TTL.
 */
const RENEW_MARGIN_MS = 5 * 60 * 1000;

export async function uploadFile(
  file: File,
  target: UploadTargetRef,
  options: UploadOptions = {},
): Promise<UploadResult> {
  const { signal, onProgress, onSessionOpened, resumeSessionId, signedFromFileId } = options;

  let phase: UploadPhase = 'opening';
  const emit = () => {
    onProgress?.({
      phase,
      bytesSent: sent.reduce((total, n) => total + n, 0),
      totalBytes: file.size,
    });
  };

  let sent: number[] = [];
  emit();

  const view = await openSession(target, file, resumeSessionId);
  const sessionId = view.session.id;
  onSessionOpened?.(sessionId);

  // The credential lives in one mutable cell rather than being captured by each
  // worker, which is what lets a renewal reach every block not yet dispatched
  // without touching one already in flight (SEC-3).
  let credential = view.upload;
  let renewal: Promise<void> | undefined;

  const blockSize = credential.blockSizeBytes;
  const blockCount = Math.ceil(file.size / blockSize);
  const lengthOf = (index: number) => Math.min(blockSize, file.size - index * blockSize);
  sent = Array.from({ length: blockCount }, () => 0);

  async function refreshCredential(force: boolean): Promise<void> {
    if (!force && Date.parse(credential.expiresAt) - Date.now() > RENEW_MARGIN_MS) return;

    // One renewal for all three workers. Set before any `await`, so two workers
    // arriving together share the request rather than racing to make two.
    renewal ??= renewUploadSession(sessionId)
      .then((renewed) => {
        credential = renewed.upload;
      })
      .finally(() => {
        renewal = undefined;
      });

    const inFlight = renewal;
    await inFlight;
  }

  const alreadyStaged =
    resumeSessionId === undefined ? new Set<string>() : await staged(credential.url, signal);

  const outstanding: number[] = [];
  for (let index = 0; index < blockCount; index += 1) {
    if (alreadyStaged.has(blockIdFor(index))) {
      // Paid for already — count it, so a resumed transfer picks up where it
      // stopped instead of showing the person a bar that restarted at zero.
      sent[index] = lengthOf(index);
    } else {
      outstanding.push(index);
    }
  }

  phase = 'staging';
  emit();

  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(BLOCK_CONCURRENCY, outstanding.length) },
    async () => {
      for (;;) {
        throwIfAborted(signal);

        const next = outstanding[cursor];
        cursor += 1;
        if (next === undefined) return;

        await stageWithRetry(next);
      }
    },
  );

  await Promise.all(workers);

  async function stageWithRetry(index: number): Promise<void> {
    const start = index * blockSize;
    const blockId = blockIdFor(index);

    for (let attempt = 1; ; attempt += 1) {
      await refreshCredential(false);
      throwIfAborted(signal);

      try {
        await stageBlock(credential.url, blockId, file.slice(start, start + lengthOf(index)), {
          ...(signal === undefined ? {} : { signal }),
          onProgress: (bytes) => {
            sent[index] = Math.min(bytes, lengthOf(index));
            emit();
          },
        });

        sent[index] = lengthOf(index);
        emit();
        return;
      } catch (error) {
        if (isAbort(error)) throw error;

        const status = error instanceof BlobError ? error.status : 0;

        // A clock skewed by more than the margin expires a credential the
        // client still believes in. One forced renewal is the backstop.
        if (status === 403) await refreshCredential(true);

        if (attempt >= BLOCK_ATTEMPTS || !worthRetrying(status)) {
          throw new UploadFailure(`Sending ${file.name} failed.`, {
            status,
            // Never permanent: the session and every staged block survive, so
            // the person retrying resumes rather than starting over.
            permanent: false,
            sessionId,
          });
        }

        sent[index] = 0;
        emit();
      }
    }
  }

  phase = 'committing';
  emit();

  try {
    await commitBlockList(
      credential.url,
      Array.from({ length: blockCount }, (_, index) => blockIdFor(index)),
      { ...(signal === undefined ? {} : { signal }) },
    );
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new UploadFailure(`Assembling ${file.name} failed.`, {
      status: error instanceof BlobError ? error.status : 0,
      permanent: false,
      sessionId,
    });
  }

  phase = 'finalizing';
  emit();

  try {
    await renewUploadSession(sessionId, { status: 'committed' });
    // `file.type` is the browser's guess and the API treats it as advisory; an
    // empty string is no guess at all, and is better left unsaid. The same rule
    // applies to the source handle: absent means "not signed from anything",
    // and sending an empty string would be a claim rather than a silence.
    const finalizeBody = {
      ...(file.type === '' ? {} : { contentType: file.type }),
      ...(signedFromFileId === undefined ? {} : { signedFromFileId }),
    };
    await finalizeUpload(
      sessionId,
      Object.keys(finalizeBody).length === 0 ? undefined : finalizeBody,
    );
  } catch (error) {
    throw new UploadFailure(`Finishing ${file.name} failed.`, {
      status: error instanceof ApiError ? error.status : 0,
      // A 409 here means the blob is not committed or its size disagrees — both
      // leave the session alive on purpose, so the upload is still resumable.
      permanent: error instanceof ApiError && isPermanent(error.status),
      sessionId,
      ...detailOf(error),
    });
  }

  return { sessionId, fileId: view.session.fileId };
}

/**
 * Open a new session, or take over an existing one.
 *
 * The two branches differ in more than the verb. A refusal on the way in is
 * permanent for a reason the person can act on — a observer space (CON-13),
 * an agency with no storage yet (CON-16) — and a 409 on the resume branch means
 * the session is already finished, which no amount of retrying will undo.
 */
async function openSession(
  target: UploadTargetRef,
  file: File,
  resumeSessionId: string | undefined,
): Promise<UploadSessionView> {
  try {
    return resumeSessionId === undefined
      ? await createUploadSession(target.spaceId, target.folderId, {
          fileName: file.name,
          sizeBytes: file.size,
        })
      : await renewUploadSession(resumeSessionId);
  } catch (error) {
    const status = error instanceof ApiError ? error.status : 0;
    const permanent = isPermanent(status) || (resumeSessionId !== undefined && status === 409);

    throw new UploadFailure(`Could not start sending ${file.name}.`, {
      status,
      permanent,
      // Only on the resume branch: a session that never opened has no id to
      // carry, and inventing one would make the next retry a resume of nothing.
      ...(resumeSessionId === undefined ? {} : { sessionId: resumeSessionId }),
      ...detailOf(error),
    });
  }
}

/** What already crossed the wire. A failure here is not fatal: re-send everything. */
async function staged(sasUrl: string, signal: AbortSignal | undefined): Promise<Set<string>> {
  try {
    return await listStagedBlocks(sasUrl, { ...(signal === undefined ? {} : { signal }) });
  } catch (error) {
    if (isAbort(error)) throw error;
    return new Set();
  }
}

/** 4xx is the server declining, not the wire failing — with the usual exceptions. */
function worthRetrying(status: number): boolean {
  return status === 0 || status === 403 || status === 408 || status === 429 || status >= 500;
}

/**
 * A refusal no retry can lift.
 *
 * `413` is PLN-6's per-file ceiling, and it belongs here for the plainest
 * reason on the list: a file larger than the plan allows will be exactly as
 * large on the next attempt. It arrives from both of the API's checkpoints —
 * the declared size when the session opens, and the committed length at
 * finalize, where the session has already been abandoned so there is nothing
 * left to resume.
 */
function isPermanent(status: number): boolean {
  return status === 400 || status === 401 || status === 403 || status === 404 || status === 413;
}

/**
 * The API's own words, when it gave any.
 *
 * Returns a spreadable fragment rather than a string, so "the API said nothing"
 * stays distinguishable from "the API said something empty" — the caller has
 * copy of its own for the first case and must not overwrite the second.
 */
function detailOf(error: unknown): { detail?: string } {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body !== null) {
    const body = error.body as { error?: { message?: unknown } };
    if (typeof body.error?.message === 'string' && body.error.message !== '') {
      return { detail: body.error.message };
    }
  }

  return {};
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('The upload was cancelled.', 'AbortError');
}
